import {EventEmitter, Injectable, NgModule, Type} from '@angular/core';
import {Route, Routes} from '@angular/router';
import {TabbingService} from '../../tabbing/services/tabbing.service';
import {ElectronService} from 'ngx-electron';
import {Tab} from '../../tabbing/data/tab';
import clone from 'just-clone';
import {LoggerService} from '../../logging/services/logger.service';
import {LogLevel} from '../../logging/data/logLevel';
import {LogSourceType} from '../../logging/data/logSourceType';
import {LogSource} from '../../logging/data/logSource';
import {AppRoutes} from '../../routing/app.routes';
import {RouteSolverService} from './route-solver.service';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {

  private routeChange: EventEmitter<Tab> = new EventEmitter<Tab>();


  get currentRoute(): SimpleNavigation {
    return clone(this.tabbingService.visibleTab.navigationData);
  }

  constructor(private tabbingService: TabbingService,
              private electronService: ElectronService,
              private loggerService: LoggerService,
              private routeSolverService: RouteSolverService) {
    this.tabbingService.tabsLoaded.subscribe((tabs) => {
      for (const tab of tabs) {
        this.navigateTab(tab, [tab.navigationData.route.path], undefined);
      }
    });
  }

  // tslint:disable-next-line:ban-types
  subscribe(tab: Tab, fn: () => void) {
    return this.routeChange.subscribe((t) => {
      if (t && tab) {
        if (t.id === tab.id) {
          fn();
        }
      }
    });
  }

  // tslint:disable-next-line:no-unnecessary-initializer
  navigate(tab: Tab, path: string[], options: NavigationOptions = undefined) {

    // Options initialization
    if (options === undefined) {
      options = new NavigationOptions();
    } else {
      options = clone(options);
    }
    // Making sure that paths are split
    path = this.splitPaths(path);
    // Electron specific
    if (this.electronService.isElectronApp) {
      if (!options.header) {
        options.header = path[0][0].toUpperCase() + path[0].substr(1);
      }
      if (!options.icon) {
        options.icon = this.getIcon(options.header);
      }
      if (options.header) {
        tab.header = options.header;
      }
      if (options.icon) {
        tab.icon = options.icon;
      }
    }
    // Route dissection
    tab.navigationData.url = '/' + path.join('/');
    const queryIndex = tab.navigationData.url.indexOf('?');
    let routeWithoutQuery = tab.navigationData.url;
    if (queryIndex !== -1) {
      routeWithoutQuery = tab.navigationData.url.substr(0, queryIndex);
    }

    // Extracting params
    if (routeWithoutQuery.startsWith('/')) {
      routeWithoutQuery = routeWithoutQuery.substr(1);
    }
    const params = routeWithoutQuery.split('/');
    tab.navigationData.params = params;
    const routeSplits = routeWithoutQuery.split('/');
    tab.navigationData.splits = routeSplits;

    // Extracting query params
    if (!options.queryParams) {
      const query = tab.navigationData.url.substr(queryIndex + 1);
      const queryParts = query.split('&');
      tab.navigationData.queryParams = {};
      for (const part of queryParts) {
        const keyValue = part.split('=');
        if (keyValue.length === 2) {
          tab.navigationData.queryParams[keyValue[0]] = keyValue[1];
        } else {
          tab.navigationData.queryParams[keyValue[0]] = '';
        }
      }
    } else {
      tab.navigationData.queryParams = options.queryParams;
    }

    tab.navigationData.loaded = false;
    this.routeSolverService.findRoute(AppRoutes.routes, routeSplits, 0).then((routeEx) => {
      const route = routeEx as Route;
      const icon = routeEx.icon;
      tab.navigationData.route = route;
      tab.navigationData.loaded = true;
      tab.icon = icon;
      this.routeChange.emit(tab);
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.NavigationService, 'Route changed to ' + tab.navigationData.url);
    });
  }

  // tslint:disable-next-line:no-unnecessary-initializer
  navigateInNewTab(path: string[], options: NavigationOptions = undefined) {
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.NavigationService, 'Navigating to ' + path.join('/') + ' in new tab');
    if (options === undefined) {
      options = new NavigationOptions();
    } else {
      options = clone(options);
    }
    if (this.electronService.isElectronApp) {
      const tab = this.tabbingService.addTab();
      setTimeout(() => {
        this.navigate(tab, path, options);
      }, 0);
    } else {
      window.open(window.location.origin + '/' + path.join('/'), '_blank');
    }
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.NavigationService, 'Navigated to ' + path.join('/') + ' in new tab');
  }

  navigateTab(tab: Tab, path: string[], options: NavigationOptions | undefined) {
    if (options === undefined) {
      options = new NavigationOptions();
    } else {
      options = clone(options);
    }
    this.navigate(tab, path, options);
  }

  splitPaths(paths: string[]): string[] {
    const result: string[] = [];
    for (const path of paths) {
      const split = path.split('/');
      for (const part of split) {
        if (part !== '') {
          result.push(part);
        }
      }
    }
    return result;
  }



  setHeader(header: string) {
    if (this.electronService.isElectronApp) {
      this.tabbingService.visibleTab.header = header;
    } else {
      document.title = header;
    }
  }

  private getIcon(header: string) {
    return this.tabbingService.visibleTab.icon;
  }
}

export class SimpleNavigation {
  url: string;
  params: string[];
  queryParams: any;
  splits: string[];
  route: Route;
  loaded: boolean = false;

  static getDefault(): SimpleNavigation {
    const nav = new SimpleNavigation();
    nav.url = '/home';
    nav.params = [];
    return nav;
  }
}

export class NavigationOptions {
  header?: string;
  icon?: string;
  queryParams?: any;
}
